class AlgoliaPrice extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.isSalePricing = this.isSalePricing.bind(this);
    this.render = this.render.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      fromPriceHtml: this.getAttribute('from-price-html'),
      priceRangeHtml: this.getAttribute('price-range-html'),
      id: this.getAttribute('id'),
      msrpPrice: this.getAttribute('msrp-price'),
      onSale: this.getAttribute('on-sale'),
      priceClass: this.getAttribute('price-class'),
      priceClassMsrp: this.getAttribute('price-class-msrp'),
      priceClassRegular: this.getAttribute('price-class-regular'),
      priceClassSale: this.getAttribute('price-class-sale'),
      regularPrice: this.getAttribute('regular-price'),
      saleBadgeColorScheme: this.getAttribute('sale-badge-color-scheme'),
      salePrice: this.getAttribute('sale-price'),
      showBadges: this.getAttribute('show-badges').toLowerCase() === 'true',
      showMsrp: this.getAttribute('show-msrp').toLowerCase() === 'true',
      showPrice: this.getAttribute('show-price').toLowerCase() === 'true',
      soldOut: this.getAttribute('sold-out'),
      soldOutBadgeColorScheme: this.getAttribute('sold-out-badge-color-scheme'),
      strikeMsrp: this.getAttribute('strike-msrp').toLowerCase() === 'true',
      yAdjustedMap: this.getAttribute('y-adjusted-map')
    };
  }

  /**
   * Checks if the item has sale pricing based on trove facets and adjusted maps.
   * @param {object} item - The item object containing meta information.
   * @returns {boolean} Returns true if the item has sale pricing; otherwise, false.
   */
  isSalePricing(item) {
    try {
      const troveFacets = item?.meta['trove-facets'];
      const markdownReason = troveFacets?.markdownReason || [];

      const adjustedMaps = JSON.parse(this.settings.yAdjustedMap);
      return adjustedMaps.some(adjustedMap => markdownReason.includes(adjustedMap.yadjusted_code));
    } catch {
      return false;
    }
  }

  /**
   * Renders the price content.
   * @param {object} item - The item object containing hit information.
   * @returns {string} The rendered price.
   */
  render(item){
    const troveFacets = item?.meta['trove-facets'];
    const trovePricing = item?.meta['trove-pricing'];
    const msrpPrice = trovePricing?.msrp_price;

    const catalogItemType = troveFacets?.catalogItemType?.toString() || '';
    const isMixedStyles = catalogItemType.includes('OPEN_SKU');
    /* All Algolia prices are already divided by 100, so when using the functions we've created, we need to multiply this price by 100 to reverse the operation. */
    const price = item.price * 100;

    const priceMin = item.variants_min_price * 100;
    const priceMax = item.variants_max_price * 100;
    const priceVaries = priceMin !== priceMax;

    const compareAtPrice = msrpPrice || price;
    const compareAtPriceMin = item.variants_compare_at_price_min;
    const compareAtPriceMax = item.variants_compare_at_price_max;
    const compareAtPriceVaries = compareAtPriceMin !== compareAtPriceMax;

    const formattedPrice = formatMoney(price);
    const formattedPriceMin = formatMoney(priceMin);
    const formattedPriceMax = formatMoney(priceMax);
    /* This doesn't apply for the msrp price since it comes from our meta-fields. */
    const formattedCompareAtPrice =  msrpPrice ? formatMoney(msrpPrice) : formattedPrice;

    const formattedFromPriceHtml = this.settings.fromPriceHtml.replace('min_price', formattedPriceMin).replace('max_price', formattedPriceMax);
    const formattedMoneyPrice = priceVaries ? formattedFromPriceHtml : formattedPrice;

    const salePricing = this.isSalePricing(item);


    // div wrapper
    const priceWrapperDiv = document.createElement('div');
    priceWrapperDiv.classList.add(
      'price',
    );
    if ( this.settings.priceClass ) priceWrapperDiv.classList.add(this.settings.priceClass);
    if ( !priceVaries && compareAtPriceVaries ) priceWrapperDiv.classList.add("price--no-compare");
    if ( this.settings.showBadges ) priceWrapperDiv.classList.add("price--show-badge");
    if ( salePricing ) priceWrapperDiv.classList.add("price--on-sale");

    // div container
    const priceContainerDiv = document.createElement('div');
    priceContainerDiv.classList.add('price__container');

    priceWrapperDiv.appendChild(priceContainerDiv);

    // price div
    const priceCurrentDiv = document.createElement('div');
    if ( salePricing ) priceCurrentDiv.classList.add('price__sale');
    priceCurrentDiv.classList.add('price__current');

    priceContainerDiv.appendChild(priceCurrentDiv);

    // price span
    const priceItemCurrentSpan = document.createElement('span');
    priceItemCurrentSpan.classList.add('price-item--current');

    if ( salePricing && this.settings.priceClassSale ) priceItemCurrentSpan.classList.add(this.settings.priceClassSale, 'price-item--sale');
    else if (this.settings.priceClassRegular) priceItemCurrentSpan.classList.add(this.settings.priceClassRegular);

    priceCurrentDiv.appendChild(priceItemCurrentSpan);

    // price 1st span
    const price1stSpan = document.createElement('span');
    if (!salePricing) {
      price1stSpan.textContent = this.settings.regularPrice;
      priceItemCurrentSpan.appendChild(price1stSpan);
    }

    // price 2nd span
    const price2ndSpan = document.createElement('span');
    price2ndSpan.classList.add('price-item');
    if (priceVaries) {
      price2ndSpan.classList.add('price-item--range');
      price2ndSpan.ariaHidden = true;
    }
    priceItemCurrentSpan.appendChild(price2ndSpan);

    // price 3rd span
    const price3rdSpan = document.createElement('span');
    price3rdSpan.classList.add('price-item');
    price3rdSpan.id = `price-item-${item.id}`;
    price3rdSpan.textContent = this.settings.showPrice ? ` ${formattedMoneyPrice}` : "";
    price2ndSpan.appendChild(price3rdSpan);

    // price range for screen readers
    if (priceVaries) {
      const screenReaderPriceSpan = document.createElement('span');
      screenReaderPriceSpan.classList.add('visually-hidden');
      screenReaderPriceSpan.textContent = this.settings.priceRangeHtml.replace('min_price', formattedPriceMin).replace('max_price', formattedPriceMax);
      price2ndSpan.appendChild(screenReaderPriceSpan);
    }

    // msrp price span
    const msrpPriceSpan = document.createElement('span');
    msrpPriceSpan.classList.add(
      'price-item--msrp'
    );

    if ( this.settings.priceClassMsrp ) msrpPriceSpan.classList.add(this.settings.priceClassMsrp);
    if ( !this.settings.showMsrp || compareAtPrice <= price || isMixedStyles ) msrpPriceSpan.classList.add('hidden');
    if ( this.settings.strikeMsrp ) msrpPriceSpan.classList.add('p-strikethrough');

    msrpPriceSpan.id = `compare-at-price-container-${item.id}`;

    const msrpPrice2ndSpan = document.createElement('span');
    msrpPrice2ndSpan.textContent = this.settings.msrpPrice;

    msrpPriceSpan.appendChild(msrpPrice2ndSpan);

    const msrpPrice3rdSpan = document.createElement('span');
    msrpPrice3rdSpan.classList.add('price-item');
    msrpPrice3rdSpan.id = `compare-at-price-item-${item.id}`;
    msrpPrice3rdSpan.textContent = ` ${formattedCompareAtPrice}`;

    msrpPriceSpan.appendChild(msrpPrice3rdSpan);

    priceCurrentDiv.appendChild(msrpPriceSpan);

    if (this.settings.showBadges) {
      const badgeSaleSpan = document.createElement('span');
      badgeSaleSpan.classList.add('badge', 'price__badge-sale', `color-${this.settings.saleBadgeColorScheme}`);
      badgeSaleSpan.textContent = this.settings.onSale;

      priceWrapperDiv.appendChild(badgeSaleSpan);

      const badgeSoldOutSpan = document.createElement('span');
      badgeSoldOutSpan.classList.add('badge', 'price__badge-sold-out', `color-${this.settings.soldOutBadgeColorScheme}`);
      badgeSoldOutSpan.textContent = this.settings.soldOut;

      priceWrapperDiv.appendChild(badgeSoldOutSpan);
    }

    return priceWrapperDiv;
  }
}

customElements.define("algolia-price", AlgoliaPrice);
